import { useCallback, useEffect, useState } from 'react';
import { getWorkerCVDownloadUrl } from '@services/api/filesApi';

/**
 * Resolves a viewable/downloadable URL for a worker resume.
 *
 * When the worker still has a public (CDN) `resumeUrl` it is used directly — no
 * request is made. Otherwise a short-lived presigned GET URL is fetched lazily
 * from the id-based endpoint and the inline preview is loaded automatically.
 */
export default function useResumeFileDownload(
  resumeId?: number,
  resumeUrl?: string
) {
  const [fetchedUrl, setFetchedUrl] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const fetchUrl = useCallback(async () => {
    if (!resumeId) {
      return undefined;
    }
    setIsLoading(true);
    setError(undefined);
    try {
      const { url } = await getWorkerCVDownloadUrl(resumeId);
      return url;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      setError(
        status === 404
          ? 'This resume is currently unavailable.'
          : 'Could not load the resume. Please try again.'
      );
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [resumeId]);

  // Only presign via the endpoint when there is no direct CDN url to use.
  useEffect(() => {
    setFetchedUrl(undefined);
    if (resumeUrl || !resumeId) {
      return undefined;
    }
    let active = true;
    fetchUrl().then((url) => {
      if (active && url) {
        setFetchedUrl(url);
      }
    });
    return () => {
      active = false;
    };
  }, [resumeId, resumeUrl, fetchUrl]);

  const previewUrl = resumeUrl ?? fetchedUrl;

  const download = useCallback(async () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener');
      return;
    }
    const url = await fetchUrl();
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }, [previewUrl, fetchUrl]);

  return { previewUrl, isLoading, error, download };
}
